import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, AED_short, AED } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, ExternalLink, ShieldAlert, Target, TrendingUp, MessageCircle, Check, SkipForward, RefreshCcw, Info } from 'lucide-react';
import MapPicker from '../components/MapPicker';
import AIRefiner from '../components/AIRefiner';
import PlacesAutocomplete from '../components/PlacesAutocomplete';

const PROPERTY_TYPES = [
  { v: 'apartment', l: 'Apartment' },
  { v: 'penthouse', l: 'Penthouse' },
  { v: 'townhouse', l: 'Townhouse' },
  { v: 'villa', l: 'Villa' },
  { v: 'commercial_office', l: 'Commercial · Office' },
  { v: 'retail', l: 'Retail' },
  { v: 'plot', l: 'Land / Plot' },
];
const CONDITIONS = [
  { v: 'excellent', l: 'Excellent — move-in ready' },
  { v: 'good', l: 'Good — light refresh only' },
  { v: 'needs_refresh', l: 'Needs refresh — paint / soft finishes' },
  { v: 'needs_refurb', l: 'Needs full refurb — kitchen / baths' },
  { v: 'distressed', l: 'Distressed — down to shell' },
];
const FURNISHED = [
  { v: 'furnished', l: 'Furnished' },
  { v: 'semi_furnished', l: 'Semi-furnished' },
  { v: 'unfurnished', l: 'Unfurnished' },
];

export default function DealAnalyzer() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    property_type: 'apartment', community: 'Dubai Marina', building: '', unit_number: '',
    bedrooms: 2, bathrooms: 2, size_sqft: 1200, plot_size_sqft: '',
    view: '', floor: '', condition: 'good', furnished: 'unfurnished',
    asking_price_aed: 2000000, listing_url: '', notes: '',
    lat: null, lng: null, address: '',
    // Tenancy at time of acquisition — swings flip-vs-hold verdicts
    tenancy_status: 'unknown',
    current_annual_rent_aed: '',
    current_lease_end_date: '',
  });

  // Prefill from URL query string (used by "Send to Deal Analyzer" from Auctions)
  useEffect(() => {
    if (!params || params.toString().length === 0) return;
    const patch = {};
    ['property_type','community','building','unit_number','view','floor','condition','furnished','listing_url','notes']
      .forEach(k => { const v = params.get(k); if (v) patch[k] = v; });
    ['bedrooms','bathrooms','size_sqft','plot_size_sqft','asking_price_aed','lat','lng']
      .forEach(k => { const v = params.get(k); if (v && !isNaN(parseFloat(v))) patch[k] = parseFloat(v); });
    // Auctions send "title" — use it as building fallback if none present
    const title = params.get('title');
    if (title && !patch.building) patch.building = title;
    if (Object.keys(patch).length) setForm(f => ({ ...f, ...patch }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qaPairs, setQaPairs] = useState([]);     // accumulated answers across refinements
  const [refining, setRefining] = useState(false);
  const [refinedCount, setRefinedCount] = useState(0);

  const isVilla = ['villa','townhouse','plot'].includes(form.property_type);

  const analyze = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null); setQaPairs([]); setRefinedCount(0);
    try {
      const r = await api.post('/deal/analyze', form);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const refineWithAnswers = async (newAnswers) => {
    setRefining(true);
    const combined = [...qaPairs, ...newAnswers];
    try {
      const r = await api.post('/deal/analyze', { ...form, qa_pairs: combined });
      setResult(r.data);
      setQaPairs(combined);
      setRefinedCount(c => c + 1);
    } finally { setRefining(false); }
  };

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="deal-analyzer-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Market Intelligence · Iris AI</div>
        <h1 className="font-serif text-4xl">Deal Analyzer</h1>
        <p className="text-slate-600 mt-2">Paste a listing URL or enter details. Iris AI estimates fair price, ARV, refurb ceiling & deal score — grounded in real Dubai DLD comps + live listings.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <motion.form onSubmit={analyze} className="glass p-6 space-y-3 lg:col-span-2" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}>
          <div className="overline">Property details</div>
          <label className="block text-sm">Property type
            <select value={form.property_type} onChange={(e)=>setForm({...form, property_type:e.target.value})} data-testid="da-type"
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              {PROPERTY_TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="col-span-2">Community
              <PlacesAutocomplete
                testid="da-community"
                value={form.community}
                onChange={(v) => setForm({ ...form, community: v })}
                onPlace={(p) => setForm({ ...form, community: p.name || p.address, lat: p.lat, lng: p.lng, address: p.address })}
                placeholder="Search Dubai communities…"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"
                autoPickOnBlur
              />
            </label>
            <label>Building / Project
              <PlacesAutocomplete
                testid="da-building"
                value={form.building}
                onChange={(v) => setForm({ ...form, building: v })}
                onPlace={(p) => setForm({ ...form, building: p.name || p.address, lat: p.lat || form.lat, lng: p.lng || form.lng })}
                placeholder="Building or project name"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"
                autoPickOnBlur
              />
            </label>
            <label>Unit
              <input value={form.unit_number} onChange={(e)=>setForm({...form, unit_number:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            {form.property_type !== 'plot' && <>
              <label>Beds
                <input type="number" value={form.bedrooms} onChange={(e)=>setForm({...form, bedrooms:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label>Baths
                <input type="number" step="0.5" value={form.bathrooms} onChange={(e)=>setForm({...form, bathrooms:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label>Built-up (sqft)
                <input type="number" value={form.size_sqft} onChange={(e)=>setForm({...form, size_sqft:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            </>}
            {isVilla && (
              <label>Plot (sqft)
                <input type="number" value={form.plot_size_sqft} onChange={(e)=>setForm({...form, plot_size_sqft:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            )}
            <label>View / orientation
              <input value={form.view} onChange={(e)=>setForm({...form, view:e.target.value})} placeholder="Golf, Marina, Burj…" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label>Floor
              <input value={form.floor} onChange={(e)=>setForm({...form, floor:e.target.value})} placeholder="e.g. 12 / high" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label className="col-span-2">Condition
              <select value={form.condition} onChange={(e)=>setForm({...form, condition:e.target.value})} data-testid="da-condition"
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                {CONDITIONS.map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </label>
            <label className="col-span-2">Furnishing
              <select value={form.furnished} onChange={(e)=>setForm({...form, furnished:e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                {FURNISHED.map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </label>
            {/* Tenancy signal — a sitting tenant on above-market or below-market rent can
                move a deal's verdict from BUY to HOLD (or the other way). */}
            <label className="col-span-2">Tenancy status
              <select value={form.tenancy_status}
                       onChange={(e)=>setForm({...form, tenancy_status: e.target.value})}
                       data-testid="da-tenancy-status"
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                <option value="unknown">Unknown / not asked yet</option>
                <option value="vacant">Vacant — vacant possession on transfer</option>
                <option value="tenanted">Tenanted — sitting tenant / lease in place</option>
              </select>
            </label>
            {form.tenancy_status === 'tenanted' && (
              <>
                <label>Current annual rent (AED)
                  <input type="number" min="0" step="1000"
                          value={form.current_annual_rent_aed}
                          onChange={(e)=>setForm({...form, current_annual_rent_aed: e.target.value})}
                          data-testid="da-current-rent"
                          placeholder="e.g. 120000"
                          className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80 font-mono"/>
                </label>
                <label>Lease ends
                  <input type="date"
                          value={form.current_lease_end_date}
                          onChange={(e)=>setForm({...form, current_lease_end_date: e.target.value})}
                          data-testid="da-lease-end"
                          className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
                </label>
              </>
            )}
            <label className="col-span-2">Asking price (AED)
              <input type="number" data-testid="da-price" value={form.asking_price_aed} onChange={(e)=>setForm({...form, asking_price_aed:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label className="col-span-2">Listing URL
              <input value={form.listing_url} onChange={(e)=>setForm({...form, listing_url:e.target.value})} placeholder="https://…" className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label className="col-span-2">Notes
              <textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
          </div>
          <button type="submit" disabled={loading} data-testid="da-analyze-btn" className="btn-gold w-full flex items-center justify-center gap-2 mt-3">
            {loading ? <><Loader2 size={16} className="animate-spin"/> Iris AI analyzing…</> : <><Sparkles size={16}/> Analyze with Iris AI</>}
          </button>
        </motion.form>

        <div className="lg:col-span-3">
          <MapPicker
            query={`${form.building || ''} ${form.community}`}
            lat={form.lat}
            lng={form.lng}
            height={520}
            onPinChange={(d) => {
              setForm(f => ({
                ...f,
                lat: d.lat,
                lng: d.lng,
                address: d.formatted_address,
                // If community field is empty or the same as default hint, adopt the pin's community
                community: (f.community && f.community !== 'Dubai Marina') ? f.community : (d.community || d.emirate || f.community),
              }));
            }}
          />
        </div>
      </div>

      {/* Full-width results below the form */}
      <div className="glass p-8" data-testid="da-result">
        {!result && !loading && <div className="text-center text-slate-500 py-10">Enter property details above and click <b>Analyze with Iris AI</b> to see the full breakdown.</div>}
        {loading && <div className="text-center py-16 flex flex-col items-center gap-3"><Loader2 size={40} className="animate-spin text-[#679286]"/><div className="text-slate-600">Iris AI is analyzing recent Bayut, PF & DLD data…</div></div>}
        {result && <ResultView r={result} form={form} onRefine={refineWithAnswers} refining={refining} refinedCount={refinedCount}/>}
      </div>
    </div>
  );
}

function ResultView({ r, form, onRefine, refining, refinedCount }) {
  return (
    <div className="space-y-8" data-testid="da-result-content">
      {/* Top-line numbers */}
      <div>
        <div className="overline mb-4 flex items-center gap-2">
          Iris AI valuation
          {refinedCount > 0 && (
            <span className="normal-case tracking-normal text-[10px] font-semibold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-full">
              Refined ×{refinedCount}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <StatCard label="Fair price" value={AED_short(r.fair_price_aed)} tint="from-[#EEF2F0] to-[#DDE7E1]"/>
          <StatCard label="After-refurb value" value={AED_short(r.arv_aed)} tint="from-[#F0EEE7] to-[#E5E1D2]"/>
          <StatCard label="Refurb budget" value={AED_short(r.refurb_budget_aed)} tint="from-[#EEEFF3] to-[#DDE0E7]"/>
          <DealScoreCard score={r.deal_score} rationale={r.deal_score_rationale}/>
        </div>
      </div>

      {/* Market benchmark — real Dubai comps from platform DB */}
      {r.market_context?.has_data && <MarketBenchmark mc={r.market_context} asking={form?.asking_price_aed} size={form?.size_sqft}/>}

      {/* Interactive Q&A refiner */}
      {r.clarifying_questions?.length > 0 && onRefine && (
        <AIRefiner
          questions={r.clarifying_questions}
          onRefine={onRefine}
          refining={refining}
          refinedCount={refinedCount}
        />
      )}

      {/* Guardrails */}
      {(r.target_purchase_price_aed || r.walk_away_price_aed || r.max_refurb_spend_aed) && (
        <div className="glass p-6 bg-white/80">
          <div className="overline mb-4 flex items-center gap-2"><ShieldAlert size={12} className="text-[#B45309]"/> Iris AI guardrails — don't cross these</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {r.target_purchase_price_aed && <Guard icon={Target} label="Target offer" value={AED(r.target_purchase_price_aed)} tone="emerald"/>}
            {r.walk_away_price_aed && <Guard icon={ShieldAlert} label="Walk-away price" value={AED(r.walk_away_price_aed)} tone="red"/>}
            {r.max_refurb_spend_aed && <Guard icon={Target} label="Max refurb ceiling" value={AED(r.max_refurb_spend_aed)} tone="amber"/>}
            {r.min_profit_target_aed && <Guard icon={TrendingUp} label="Min profit target" value={AED(r.min_profit_target_aed)} tone="emerald"/>}
          </div>
          {r.spend_guardrails?.length > 0 && (
            <ul className="mt-4 text-sm text-slate-700 space-y-1 list-disc pl-5">
              {r.spend_guardrails.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Summary */}
      {r.summary && (
        <div className="p-6 rounded-2xl bg-white/60 border border-white/70">
          <div className="overline mb-2">Analysis summary</div>
          <div className="text-base text-slate-800 leading-relaxed">{r.summary}</div>
        </div>
      )}

      {/* Positives / Risks / Premium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {r.positives?.length > 0 && <ColList title="Positives" items={r.positives} tone="emerald"/>}
        {r.risks?.length > 0 && <ColList title="Risks" items={r.risks} tone="red"/>}
        {r.premium_factors?.length > 0 && <ColList title="Premium factors" items={r.premium_factors} tone="sage"/>}
      </div>

      {/* Comparables — sanitised sources; only known-permanent portals */}
      {r.comparables?.length > 0 && (
        <div>
          <div className="overline mb-3">Recent sold comparables
            <span className="text-slate-400 normal-case tracking-normal text-xs ml-2">links open the reference portal — search there for the exact record</span>
          </div>
          <div className="space-y-2">
            {r.comparables.map((c, i) => <Comparable key={i} c={c} i={i}/>)}
          </div>
        </div>
      )}

      {/* Sources */}
      {r.sources?.length > 0 && (
        <div className="pt-4 border-t border-slate-200/60">
          <div className="overline mb-1">Reference portals</div>
          <div className="text-xs text-slate-500 mb-3">AI-generated references — verify exact records on the source portal.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {r.sources.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`da-source-${i}`}
                 className="p-3 bg-white/60 hover:bg-white rounded-2xl border border-white/70 flex items-center justify-between group">
                <div className="text-sm text-slate-700 flex-1 min-w-0 truncate">{s.title}</div>
                <ExternalLink size={13} className="text-slate-400 group-hover:text-[#679286] flex-shrink-0"/>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tint }) {
  return (
    <div className={`p-5 rounded-3xl bg-gradient-to-br ${tint} border border-white/50 shadow-sm`}>
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
    </div>
  );
}

function Guard({ icon: Icon, label, value, tone }) {
  const toneMap = {
    red: 'bg-red-50/70 border-red-200/70 text-red-800',
    amber: 'bg-amber-50/70 border-amber-200/70 text-amber-800',
    emerald: 'bg-emerald-50/70 border-emerald-200/70 text-emerald-800',
  };
  return (
    <div className={`p-4 rounded-2xl border ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold"><Icon size={13}/> {label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}

function ColList({ title, items, tone }) {
  const t = { emerald: 'text-emerald-700', red: 'text-red-700', sage: 'text-[#679286]' }[tone];
  return (
    <div>
      <div className={`overline mb-2 ${t}`}>{title}</div>
      <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}


function DealScoreCard({ score, rationale }) {
  const [open, setOpen] = useState(false);
  const has = !!(rationale && (rationale.verdict || rationale.breakdown?.length));
  return (
    <div className="p-5 rounded-3xl bg-gradient-to-br from-[#E7F0EB] to-[#CFDDD2] border border-white/50 shadow-sm relative">
      <div className="flex items-center justify-between">
        <div className="overline">Deal score</div>
        {has && (
          <button
            type="button"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen(o => !o)}
            data-testid="da-deal-score-info"
            className="p-1 rounded-full hover:bg-white/60 text-slate-500 hover:text-[#679286] transition-colors"
            aria-label="How is this score calculated?"
          >
            <Info size={14}/>
          </button>
        )}
      </div>
      <div className="font-serif text-3xl mt-2 text-[#679286]">{score ?? '—'}</div>
      {has && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="text-[11px] text-[#679286]/80 hover:text-[#679286] mt-2 underline decoration-dotted"
          data-testid="da-deal-score-explain"
        >
          {open ? 'Hide breakdown' : 'How is this calculated?'}
        </button>
      )}
      <AnimatePresence>
        {open && has && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-20 top-full left-0 right-0 md:left-auto md:right-0 md:w-[380px] mt-2 p-4 rounded-2xl bg-white shadow-2xl border border-slate-200/80"
            data-testid="da-deal-score-popover"
          >
            {rationale.verdict && (
              <div className="text-sm text-slate-800 font-medium mb-3 leading-snug">
                {rationale.verdict}
              </div>
            )}
            {rationale.breakdown?.length > 0 && (
              <div className="space-y-2">
                {rationale.breakdown.map((b, i) => {
                  const isNeg = String(b.delta).trim().startsWith('-');
                  return (
                    <div key={i} className="flex items-start gap-3 text-xs">
                      <span className={`font-mono font-semibold w-10 flex-shrink-0 text-right ${isNeg ? 'text-red-600' : 'text-emerald-700'}`}>
                        {b.delta}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 font-medium">{b.factor}</div>
                        {b.note && <div className="text-slate-500 mt-0.5">{b.note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400 leading-relaxed">
              Score = 50 baseline + weighted factor deltas. Above 70 = strong buy signal; below 40 = walk away.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Comparable({ c, i }) {
  const href = c.source_url || c.listing_url || null;
  const badge = c.source_name || (c.source_url ? sourceBadgeFromUrl(c.source_url) : null);
  return (
    <div className="flex items-center justify-between p-4 bg-white/60 hover:bg-white rounded-2xl border border-white/70 transition-all group"
         data-testid={`da-comparable-${i}`}>
      <div className="flex-1 min-w-0 pr-4">
        <div className="text-sm text-slate-800">{c.description}</div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
          <span>{c.date}</span>
          {badge && (
            <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-semibold">
              {badge}
            </span>
          )}
        </div>
      </div>
      <div className="text-right flex items-center gap-3">
        <span className="font-serif text-xl">{AED_short(c.price_aed)}</span>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer"
             title={`Open ${badge || 'sale record'} source`}
             className="text-xs flex items-center gap-1 text-slate-400 hover:text-[#679286]"
             data-testid={`da-comparable-source-${i}`}>
            View source <ExternalLink size={12}/>
          </a>
        ) : (
          <span className="text-[10px] text-slate-400">no source</span>
        )}
      </div>
    </div>
  );
}

function sourceBadgeFromUrl(url) {
  try {
    const h = new URL(url).hostname.replace('www.', '');
    if (h.includes('dubailand')) return 'DLD';
    if (h.includes('propertymonitor')) return 'Property Monitor';
    if (h.includes('reidin')) return 'Reidin';
    if (h.includes('bayut')) return 'Bayut';
    if (h.includes('propertyfinder')) return 'PF';
    if (h.includes('dubizzle')) return 'Dubizzle';
    return h.split('.')[0];
  } catch {
    return null;
  }
}

function MarketBenchmark({ mc, asking, size }) {
  const stats = mc?.stats_90d || {};
  const soldMedian = stats.sold_psf?.median;
  const soldCount = stats.sold_psf?.count || 0;
  const askingPsf = (asking && size) ? asking / size : null;
  const deltaPct = (soldMedian && askingPsf) ? ((askingPsf - soldMedian) / soldMedian) * 100 : null;
  const isBelow = deltaPct != null && deltaPct < -3;
  const isAbove = deltaPct != null && deltaPct > 3;
  const soldComps = mc?.sold_comps_180d || [];
  const liveComps = mc?.live_comps || [];

  const tone = isBelow ? 'emerald' : isAbove ? 'red' : 'slate';
  const bandBg = tone === 'emerald' ? 'bg-emerald-50/70 border-emerald-200'
                : tone === 'red' ? 'bg-red-50/70 border-red-200'
                : 'bg-slate-50/70 border-slate-200';
  const bandText = tone === 'emerald' ? 'text-emerald-800'
                  : tone === 'red' ? 'text-red-800' : 'text-slate-700';

  return (
    <div className="glass p-6 bg-white/70" data-testid="da-market-benchmark">
      <div className="flex items-center gap-2 mb-4">
        <span className="overline">Real market benchmark</span>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#679286]/15 text-[#679286]">
          Live Dubai data
        </span>
      </div>

      {deltaPct != null && (
        <div className={`rounded-2xl p-4 border ${bandBg} mb-4`} data-testid="da-vs-market">
          <div className={`text-sm font-semibold ${bandText}`}>
            {isBelow ? '↓ Below market by' : isAbove ? '↑ Above market by' : '≈ Priced in line with market —'}
            {' '}
            <span className="font-serif text-lg">{Math.abs(deltaPct).toFixed(1)}%</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Your asking AED {askingPsf ? Math.round(askingPsf).toLocaleString() : '—'}/sqft
            {' vs '}
            community 90-day sold median AED {soldMedian ? Math.round(soldMedian).toLocaleString() : '—'}/sqft
            {' '}({soldCount} sold comps)
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3 text-sm mb-4">
        <StatMini label="Sold count (90d)" value={soldCount} />
        <StatMini label="Median AED/sqft" value={soldMedian ? Math.round(soldMedian).toLocaleString() : '—'} />
        <StatMini label="Active listings" value={stats.supply_active_listings ?? 0}
                  sub={stats.psf_momentum_30d_pct != null ? `${stats.psf_momentum_30d_pct > 0 ? '+' : ''}${stats.psf_momentum_30d_pct}% momentum (30d)` : null}/>
      </div>

      {liveComps.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Similar live listings ({liveComps.length})</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {liveComps.slice(0, 6).map((l, i) => (
              <a key={i} href={l.url || '#'} target="_blank" rel="noreferrer"
                 className="p-3 bg-white/70 hover:bg-white rounded-2xl border border-white/70 transition group flex items-center justify-between"
                 data-testid={`da-live-comp-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">{l.building || '—'}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span>{l.beds === 0 ? 'Studio' : `${l.beds ?? '?'}BR`}</span>
                    <span>{l.sqft ? Math.round(l.sqft).toLocaleString() : '?'} sqft</span>
                    {l.completion_status && <span className="uppercase text-[9px] text-amber-600">{l.completion_status.replace('_',' ')}</span>}
                  </div>
                </div>
                <div className="text-right ml-3">
                  <div className="font-serif text-base text-[#679286]">{AED_short(l.asking_price_aed)}</div>
                  <div className="text-[10px] text-slate-400 flex items-center gap-1 justify-end">
                    Bayut <ExternalLink size={9}/>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {soldComps.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Recent sold comps ({soldComps.length}) · DLD</div>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left py-1 px-2">Date</th>
                  <th className="text-left py-1 px-2">Building</th>
                  <th className="text-right py-1 px-2">Beds</th>
                  <th className="text-right py-1 px-2">Sqft</th>
                  <th className="text-right py-1 px-2">Price</th>
                  <th className="text-right py-1 px-2">AED/sqft</th>
                </tr>
              </thead>
              <tbody>
                {soldComps.slice(0, 8).map((c, i) => (
                  <tr key={i} className="border-t border-slate-100/60" data-testid={`da-sold-comp-${i}`}>
                    <td className="py-1.5 px-2 text-slate-500">{c.date}</td>
                    <td className="py-1.5 px-2 text-slate-800 truncate max-w-[10rem]">{c.building || '—'}</td>
                    <td className="py-1.5 px-2 text-right">{c.beds === 0 ? 'Studio' : (c.beds ?? '—')}</td>
                    <td className="py-1.5 px-2 text-right">{c.sqft ? Math.round(c.sqft).toLocaleString() : '—'}</td>
                    <td className="py-1.5 px-2 text-right font-semibold text-[#679286]">{AED_short(c.price_aed)}</td>
                    <td className="py-1.5 px-2 text-right">{c.aed_per_sqft ? Math.round(c.aed_per_sqft).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, sub }) {
  return (
    <div className="p-3 rounded-2xl bg-white/70 border border-white/70">
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif text-xl mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}